import styled from "styled-components";
import { useState, useContext, useEffect } from "react";
import OrgTreeSelect from "../UI/OrgTreeSelect/OrgTreeSelect";
import { Calendar } from "primereact/calendar";
import { ReportDatabaseContext } from "../../Contexts/ReportDatabaseContext";
import { generateDateArray, formatDate } from "../../Helpers/TimeFunctions";
import { getAllTeamIdsFromOrgTreeArray } from "../../Helpers/OrgTreeFunctions";
import Papa from "papaparse";

const PageWrapper = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;

  button {
    margin: 20px auto;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid var(--accent-color-dark);
    background-color: white;
    color: var(--accent-color-dark);
    cursor: pointer;
    transition: 0.2s;
  }
  button:hover {
    background-color: var(--accent-color-dark);
    color: white;
  }
`;
const SelectWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;

  .p-inputtext {
    width: 220px;
    text-align: center;
  }
`;

export default function DataExportPage(props: Props) {
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [selectedTeam, setSelectedTeam] = useState<OrgTreeItem>();
  const { reportData, setNodesNeeded } = useContext(ReportDatabaseContext);
  const [matchingReportData, setMatchingReportData] = useState<
    ReportData[] | null
  >(null);

  useEffect(() => {
    if (!selectedTeam) return;
    setNodesNeeded((prevState) => {
      if (!prevState) return [selectedTeam];
      if (prevState.some((team) => team.teamId === selectedTeam.teamId))
        return prevState;
      return [...prevState, selectedTeam];
    });
  }, [selectedTeam]);

  useEffect(() => {
    if (reportData && selectedTeam && dates[0] && dates[1]) {
      const reportMatch = reportData.filter((report) => {
        const dateRangeArray = generateDateArray(dates[0], dates[1]);
        const firstDay = new Date(dateRangeArray[0].startDate);
        const lastDay = new Date(
          dateRangeArray[dateRangeArray.length - 1].endDate
        );

        if (
          selectedTeam.teamType === "volunteer" ||
          selectedTeam.teamType === "churchStaff" ||
          selectedTeam.teamType === "pastoralCare"
        ) {
          return (
            report.userId === selectedTeam.teamId &&
            new Date(report.reportPeriodDate) >= firstDay &&
            new Date(report.reportPeriodDate) <= lastDay
          );
        } else {
          return (
            getAllTeamIdsFromOrgTreeArray([selectedTeam]).includes(
              report.teamId
            ) &&
            new Date(report.reportPeriodDate) >= firstDay &&
            new Date(report.reportPeriodDate) <= lastDay
          );
        }
      });
      //formating date for readability
      reportMatch.forEach((report) => {
        report.reportPeriodDate = formatDate(new Date(report.reportPeriodDate));
      });
      console.log("reportMatch length", reportMatch.length);
      setMatchingReportData(reportMatch);
    }
  }, [reportData, selectedTeam, dates]);

  const downloadCSV = () => {
    if (matchingReportData) {
      const csv = Papa.unparse(matchingReportData, {
        columns: [
          "reportPeriodDate",
          "reportId",
          "bucketId",
          "lastEditDate",
          "userId",
          "firstName",
          "lastName",
          "email",
          "location",
          "teamId",
          "involvement",
          "importedData",
          "hotTopics",
          "adminAndMeetingsHours",
          "professionalDevelopmentHours",
          "crisisHours",
          "relationshipHours",
          "lifeSkillsHours",
          "traumaHours",
          "addNeedsHours",
          "healthHours",
          "mentalHealthHours",
          "prepAndEnrichAppointments",
          "weedingsConducted",
          "newPornAddictions",
          "newDFVIncidents",
          "funeralsConducted",
          "hospitalVisits",
          "suicidalIdeation",
          "nonSuicidalSelfInjury",
          "giftsFlowers",
          "meals",
          "kiloOfKindnessBags",
          "lostJobs",
          "employmentReceived",
          "totalPastoralHours",
          "totalInPersonHours",
        ],
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "report_data.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.log("noMatchingData");
    }
  };

  const downloadAllCSV = () => {
    if (reportData) {
      reportData.forEach((report) => {
        report.reportPeriodDate = formatDate(new Date(report.reportPeriodDate));
      });
      const csv = Papa.unparse(reportData, {
        columns: [
          "reportPeriodDate",
          "reportId",
          "bucketId",
          "lastEditDate",
          "userId",
          "firstName",
          "lastName",
          "email",
          "location",
          "teamId",
          "involvement",
          "importedData",
          "hotTopics",
          "adminAndMeetingsHours",
          "professionalDevelopmentHours",
          "crisisHours",
          "relationshipHours",
          "lifeSkillsHours",
          "traumaHours",
          "addNeedsHours",
          "healthHours",
          "mentalHealthHours",
          "prepAndEnrichAppointments",
          "weedingsConducted",
          "newPornAddictions",
          "newDFVIncidents",
          "funeralsConducted",
          "hospitalVisits",
          "suicidalIdeation",
          "nonSuicidalSelfInjury",
          "giftsFlowers",
          "meals",
          "kiloOfKindnessBags",
          "lostJobs",
          "employmentReceived",
          "totalPastoralHours",
          "totalInPersonHours",
        ],
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "report_data.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.log("noMatchingData");
    }
  };

  return (
    <PageWrapper>
      <SelectWrapper>
        <OrgTreeSelect
          includeUsers={true}
          selectedTeam={selectedTeam}
          teamSelect={setSelectedTeam}
        />
        <Calendar
          value={dates}
          onChange={(e) => Array.isArray(e.value) && setDates(e.value)}
          selectionMode="range"
          minDate={new Date("01-01-2018")}
          maxDate={new Date()}
          dateFormat="dd/mm/yy"
        />
      </SelectWrapper>
      <button onClick={downloadCSV}>Download CSV</button>
      <button onClick={downloadAllCSV}>Download CSV of all reports</button>
    </PageWrapper>
  );
}

interface Props {}
